import { createSelector } from '@reduxjs/toolkit';

import { BillingCycleEnum } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';

export type BillingInterval = 'monthly' | 'annually';

/**
 * Billing-derived values the GenX add-on purchase modal needs to render. `null`
 * means the source `usageLimit` slice hasn't loaded yet — consumers should show
 * a loading state rather than fall back to defaults that may not match the
 * user's actual subscription (e.g. monthly vs annual addon mismatch).
 */
export interface GenXBillingContext {
    billingInterval: BillingInterval;
    currentQuantity: number;
    planMinutes: number;
}

export function getUsageLimits(state: RootState) {
    return state.usageLimit;
}

export function getAIVOMinutesLimit(state: RootState): number | null {
    return getUsageLimits(state)?.ai_vo_minutes_limit_per_cycle;
}

export function getAIVOMinutesUsedInCycle(state: RootState): number | undefined {
    return getUsageLimits(state)?.ai_vo_minutes_used_in_cycle;
}

export function getIsAIVOCreditsExhausted(state: RootState): boolean {
    const limit = getAIVOMinutesLimit(state);
    const used = getAIVOMinutesUsedInCycle(state);
    return limit !== null && used !== undefined && used >= limit;
}

export const getGenXBillingContext = createSelector(
    [getUsageLimits],
    (usageLimit): GenXBillingContext | null => {
        if (!usageLimit?.isLoaded) return null;
        const hasSubscription = (usageLimit.animation_seconds_limit_per_cycle ?? 0) > 0;
        const trialLimit = usageLimit.trial_animation_seconds_limit ?? 0;
        const isTrialOnly = !hasSubscription && trialLimit > 0;
        const limitMinutes = Math.round((usageLimit.animation_seconds_limit_per_cycle ?? 0) / 60);
        const addonMinutes = isTrialOnly
            ? 0
            : Math.min(usageLimit.addon_animation_minutes_quantity ?? 0, limitMinutes);
        return {
            billingInterval:
                usageLimit.usage_cycle === BillingCycleEnum.ANNUAL ? 'annually' : 'monthly',
            currentQuantity: addonMinutes,
            planMinutes: isTrialOnly ? 0 : limitMinutes - addonMinutes,
        };
    },
);
